import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../App';

const SocketContext = createContext();

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Only connect if user is logged in
    if (!user || !token) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setConnected(false);
      }
      return;
    }

    // Create socket connection
    const socketInstance = io(process.env.REACT_APP_API_URL || 'https://sahayak-backend.onrender.com', {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    // Set up event listeners
    socketInstance.on('connect', () => {
      console.log('Socket connected');
      setConnected(true);
      setError(null);
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setConnected(false);
    });

    socketInstance.on('connect_error', (err) => {
      console.error('Socket connection error:', err.message);
      setError(err.message);
      setConnected(false);
    });

    socketInstance.on('error', (err) => {
      console.error('Socket error:', err);
      setError(err.message);
    });

    // Save socket instance
    setSocket(socketInstance);

    // Clean up on unmount
    return () => {
      if (socketInstance) {
        socketInstance.disconnect();
      }
    };
  }, [user, token]);

  const joinChat = (chatId) => {
    if (socket && connected) {
      socket.emit('join_chat', chatId);
    }
  };

  const sendMessage = (chatId, content) => {
    if (socket && connected) {
      socket.emit('send_message', { chatId, content });
    }
  };

  const markMessagesAsRead = (chatId) => {
    if (socket && connected) {
      socket.emit('mark_read', { chatId });
    }
  };

  return (
    <SocketContext.Provider
      value={{
        socket,
        connected,
        error,
        joinChat,
        sendMessage,
        markMessagesAsRead,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext; 